// Pieces of a message row that do not depend on what kind of conversation it
// is in: the row under a message (copy, time) and the copy button (the files
// attached to it are attachments.tsx). The Agent chat's rows and other
// conversations (cloud's topics and direct messages) draw a message with the
// same pieces.
//
// Promise:
// - The row under a message keeps its height whether shown or not, so nothing
//   moves when it appears on hover.
import { m } from "@trameria/i18n";
import type { ReactNode } from "react";
import { SMALL_ICON_BUTTON } from "../ui/button";
import { Icon } from "../ui/icon";
import { Tooltip } from "../ui/tooltip";
import { useCopiedMark } from "../ui/use-copied-mark";

export const META_ROW = "flex h-6 items-center gap-1 text-[11.5px] text-faint";

/** Shown on hover and focus of the message (its row is the `group`). */
export const REVEAL =
  "pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100";

/** Shows a hover-only piece all the time on a screen without hover (touch). */
export const TOUCH_SHOWN =
  "[@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100";

/**
 * The row under a message. What says something about the message (`lead`: not
 * read yet, its side chat) always shows; the controls and the time show on
 * hover and focus, or `always`. With `touchAlways` they always show on a touch
 * screen (the Agent chat's rows). Its edge lines up with the message's: under
 * a reply the first button starts where the text starts, under a post the
 * time ends where the bubble ends.
 */
export function MetaRow({
  lead,
  children,
  always,
  touchAlways = false,
  align = "start",
}: {
  lead?: ReactNode;
  children: ReactNode;
  always: boolean;
  touchAlways?: boolean;
  align?: "start" | "end";
}) {
  // With a lead (a waiting post's note and its buttons) the row may not fit
  // a narrow column: no word breaks inside (the old web's), and the controls
  // go down to a second line instead of the pieces folding in two over the
  // bubble (the person, 2026-10-18).
  return (
    <div
      className={`${META_ROW} ${align === "end" ? "justify-end pl-1" : "px-1"} ${
        lead ? "h-auto min-h-6 flex-wrap whitespace-nowrap" : ""
      }`}
    >
      {lead}
      <div
        className={`flex flex-none items-center gap-1 whitespace-nowrap transition-opacity ${
          always ? "" : `${REVEAL} ${touchAlways ? TOUCH_SHOWN : ""}`
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export function CopyButton({ text }: { text: string }) {
  const [done, markDone] = useCopiedMark();
  return (
    <Tooltip text={done ? m.agents_copied() : m.agents_copy()}>
      <button
        type="button"
        aria-label={m.agents_copy()}
        onClick={() => {
          void navigator.clipboard?.writeText(text).then(markDone);
        }}
        className={`${SMALL_ICON_BUTTON} text-faint`}
      >
        <Icon
          kind={done ? "check" : "copy"}
          size={14}
          stroke={done ? 1.5 : 1.3}
        />
      </button>
    </Tooltip>
  );
}
